import { Link } from "react-router-dom"
import { CONTACT, SiteLayout, StartButtons } from "./SiteLayout"

type Status = "live" | "building" | "planned"
const STATUS: Record<Status, string> = { live: "In place", building: "In progress", planned: "Planned" }

/**
 * What Nira does with what you upload. Each row says what is in place, what is in progress and
 * what is planned, as NitiAssist's Trust Centre records it for Nira ("Your documents in Nira",
 * website/src/lib/trust.ts NIRA_DOCUMENTS, and docs/dpdp/compliance-register.md). A row here may
 * only say "In place" when the register does; change them together.
 */
const COMMITMENTS: { id: string; h: string; p: string; status: Status; note?: string }[] = [
  {
    id: "no-training", status: "live",
    h: "Never used to train a model",
    p: "Nothing you upload, the facts read from it, your questions or your readings is used to train, fine-tune or evaluate any model, ours or anyone else's. There is no setting that turns this on. The model Nira uses runs on Amazon Bedrock, whose terms do not allow inputs or outputs to be used for training.",
  },
  {
    id: "purpose", status: "live",
    h: "Used only for what you asked",
    p: "A document is read to give you the reading you asked for, in its workspace, and to answer your questions in that workspace. It is not shared, sold or read for any other purpose. This is the purpose limitation of sections 4 and 6 of the Digital Personal Data Protection Act, 2023.",
  },
  {
    id: "not-asked", status: "live",
    h: "Nothing about who you are is asked",
    p: "Nira reads what the analysis needs: the section, the years, the amounts and the dates. It never asks you for your name, your GSTIN, the notice number or the DIN, and the facts you can check are only the ones that change the outcome.",
  },
  {
    id: "deletion", status: "live",
    h: "Deleted when you delete it",
    p: "Delete a workspace and its documents, the facts read from them and every reading go from your account at once. Copies are erased from our systems within 30 days, and AI debugging traces within 60 days. Backups roll off within 7 days.",
  },
  {
    id: "on-device", status: "planned",
    h: "Identifiers blacked out on your device",
    p: "Blacking out GSTINs, PAN, Aadhaar, contact and bank details, party names and addresses in your browser, before a document is uploaded, comes with Nira for firms. Today, documents are uploaded as they are, over an encrypted connection, and kept under your account.",
  },
]

/** Your documents (the public site): what Nira does with what you upload, said plainly. */
export default function SiteDocuments() {
  return (
    <SiteLayout title="Your documents">
      <section className="nst-hero slim">
        <div className="nst-wrap nst-hero-copy">
          <p className="nst-k">Your documents</p>
          <h1>Your notice is read for your answer, and for nothing else.</h1>
          <p className="nst-lede">
            What Nira does with a notice or order you upload: what it reads, what it keeps, who can see it, and what is not in place yet.
            Held under the Digital Personal Data Protection Act, 2023.
          </p>
        </div>
      </section>

      <section className="nst-sec" aria-labelledby="nsd-commit">
        <div className="nst-wrap nst-split">
          <div className="nst-side"><p className="nst-k" id="nsd-commit">What we hold ourselves to</p><span className="nst-rule" aria-hidden="true" /></div>
          <div className="nst-main">
            <ul className="nsd-rows">
              {COMMITMENTS.map((c) => (
                <li key={c.id} id={c.id}>
                  <div className="nsd-row-h"><h2>{c.h}</h2><span className={`nsd-status ${c.status}`}>{STATUS[c.status]}</span></div>
                  <p>{c.p}</p>
                  {c.note ? <p className="nsd-note">{c.note}</p> : null}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="nst-sec nst-tint" aria-labelledby="nsd-who">
        <div className="nst-wrap nst-split">
          <div className="nst-side"><p className="nst-k" id="nsd-who">Who can see it</p><span className="nst-rule" aria-hidden="true" /></div>
          <div className="nst-main">
            <p className="nst-read">You, and no other user. A workspace, its documents and its readings open only under the account that made them. A small number of authorised staff can see them to answer a support request or fix a fault (<Link to="/privacy">privacy policy</Link>).</p>
            <ul className="nst-facts">
              <li><b>A share link</b> shows the one reading you shared, and nothing else in the workspace.</li>
              <li><b>In transit and at rest</b>, what you upload is encrypted.</li>
              <li><b>Kept</b> under your account until you delete the workspace or your account.</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="nst-sec" aria-labelledby="nsd-rights">
        <div className="nst-wrap nst-split">
          <div className="nst-side"><p className="nst-k" id="nsd-rights">Your rights</p><span className="nst-rule" aria-hidden="true" /></div>
          <div className="nst-main">
            <p className="nst-read">Delete a workspace yourself, any time, from the workspace, or your whole account from the Account page. For anything else, to know what we hold, to correct it or to erase it, write to us.</p>
            <ul className="nst-facts">
              <li><b>Write to</b> <a href={`${CONTACT}%20-%20my%20data`}>contact@nitiassistai.com</a>, saying what you would like done.</li>
              <li><b>The full register</b>, every control and where it stands, is in NitiAssist's <a href="https://www.nitiassistai.com/trust" target="_blank" rel="noreferrer">Trust Centre</a>.</li>
              <li><b>How we work</b>, the rules we hold ourselves to on every answer: <Link to="/how-we-work">How we work</Link>.</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="nst-navy">
        <div className="nst-wrap nst-cta-row">
          <div className="nst-cta-copy"><h2 className="nst-h2">See it on your own notice.</h2></div>
          <StartButtons dark />
        </div>
      </section>
    </SiteLayout>
  )
}
